import { getFromDataPath, mutateByPath } from "../../../utility";
import { ArsSidebarItemData, ArsSidebarItemKind } from "../../sidebar/interface";
import { isAnalysisMethod, isGroupingFactor, isOperation } from "../../typeGuards";
import { ArsTableGroupCalculationDto, ArsTableState } from "../interface";
import { isArsTableGroupCalculationDto } from "../typeguards";
import {
  arst_analysisMethodToTableGroup,
  arst_groupingFactorToTableGroup,
  arst_operationToTableGroup,
} from "./arst_arsToTableGroup";
import { arst_checkRowHeaders } from "./arst_checkRowHeaders";
import { collectParameterPaths } from "./collectParameterPaths";
import { syncParameters } from "./syncParameters";

/**
 * Inserts dropped ARS items at a resolved position and updates their shared table state.
 *
 * @param state - Current table state containing the existing parameter peers and grouping factors
 * @param payload - Dropped sidebar items and their analysis method identifiers
 * @param groupsPath - JSON Pointer to the root or nested group list receiving the items
 * @param insertIndex - Position within the receiving list at which to insert the dropped items
 * @returns Updated state with synchronised parameters, grouping factor IDs and row headers
 */
export const insertItemAt = <T>(
  state: ArsTableState<T>,
  payload: ArsSidebarItemData[],
  groupsPath: string,
  insertIndex: number,
): ArsTableState<T> => {
  // Create new table groups from the dropped item
  const newTableGroups: ArsTableGroupCalculationDto<T>[] = payload
    .map((item) => item.dropItem)
    .map((item) => {
      return isGroupingFactor(item)
        ? arst_groupingFactorToTableGroup(item, state.settings.editorProps)
        : isOperation(item)
          ? arst_operationToTableGroup(item, state.settings.editorProps)
          : isAnalysisMethod(item)
            ? arst_analysisMethodToTableGroup(item, state.settings.editorProps)
            : null;
    })
    .filter((group): group is ArsTableGroupCalculationDto<T> => group !== null);
  if (newTableGroups.length === 0) return state;

  // Update AnalysisMethodId
  const droppedAnalysisMethodIds = [
    ...payload.filter((item) => item.kind === "analysisMethod").map((item) => item.dropItem.id),
    ...payload.filter((item) => item.kind === "operation").map((item) => item.analysisMethodId),
  ];
  const newAnalysisMethodIds = new Set<string>(
    [state.bodyData.analysisMethodId, ...droppedAnalysisMethodIds].filter(Boolean),
  );
  if (newAnalysisMethodIds.size > 1) {
    // Abort if multiple analysis methods are being inserted or if the new analysis method is different from the current one
    return state;
  }

  const currentGroups = getFromDataPath(
    groupsPath,
    state.bodyData,
    (value): value is ArsTableGroupCalculationDto<T>[] =>
      Array.isArray(value) &&
      value.every((group) => isArsTableGroupCalculationDto(group, state.settings.editorProps.isT)),
  );
  const newGroups = [...(currentGroups ?? [])];
  newGroups.splice(insertIndex, 0, ...newTableGroups);
  let newBodyData = mutateByPath(state.bodyData, groupsPath, newGroups);
  const sourcePath = newTableGroups
    .flatMap((group, index) => {
      const path = `${groupsPath}/${insertIndex + index}`;
      return [
        ...collectParameterPaths(group, path, "groupingFactor"),
        ...collectParameterPaths(group, path, "operation"),
      ];
    })
    .at(0);

  // Sync after dropping
  if (state.analysisMethod && sourcePath) {
    newBodyData = syncParameters(
      newBodyData,
      sourcePath,
      state.analysisMethod,
      state.settings.editorProps.isT,
      true,
    );
  }

  // Track new used grouping factor IDs
  const newGroupingFactorIds = new Set(state.usedGroupingFactorIds);
  payload.forEach((item) => {
    if (item.kind === ArsSidebarItemKind.GroupingFactor) {
      newGroupingFactorIds.add(item.dropItem.id);
    }
  });
  // Check and update row headers after inserting new subgroups or siblings
  newBodyData.rowHeaders = arst_checkRowHeaders(newBodyData, state.settings.editorProps);
  return {
    ...state,
    bodyData: {
      ...newBodyData,
      analysisMethodId: newAnalysisMethodIds.values().next().value ?? "",
    },
    usedGroupingFactorIds: newGroupingFactorIds,
    changed: true,
  };
};
